import React, { useEffect, useState } from "react";
import Table from "../../EntryFile/datatable";
import FeatherIcon from "feather-icons-react";
import { Link } from "react-router-dom";
import alertify from "alertifyjs";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { DeleteIcon, EditIcon } from "../../EntryFile/imagePath";
import Tabletop from "../../EntryFile/tabletop";
import PropTypes from 'prop-types';
import { MinusIcon, PlusIcon, X } from "lucide-react";
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  updateItemQuantity
} from "../../core/api/inventory";
import Swal from "sweetalert2";

const InventoryCRUD = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [quantityLoading, setQuantityLoading] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    itemName: "",
    description: "",
    quantity: 0,
    notes: "",
  });

  const [errors, setErrors] = useState({});

  // Quantity update handlers
  const handleQuantityChange = async (itemId, increment) => {
    const item = data.find((i) => i.id === itemId);
    if (!item) return;

    const newQuantity = item.quantity + increment;
    if (newQuantity < 0) return;

    setQuantityLoading((prev) => ({ ...prev, [itemId]: true }));
    
    try {
      await updateItemQuantity(itemId, increment);
      setData((prevData) =>
        prevData.map((item) =>
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        )
      );
    } catch (error) {
      alertify.error(error?.response?.data?.message || "Failed to update quantity");
    } finally {
      setQuantityLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const QuantityControl = ({ record }) => (
    <div className="d-flex align-items-center bg-light text-center" style={{width:'fit-content', borderRadius:"10px", overflow: 'hidden'}}>
      <Button
        size="sm"
        variant="light"
        className="no-hover-effect"
        onClick={() => handleQuantityChange(record.id, -1)}
        disabled={quantityLoading[record?.id]}
      >
        <MinusIcon size={13} />
      </Button>
      <span className="mx-2" style={{width:20}}>
        {quantityLoading[record?.id] ? (
          <Spinner animation="border" size="sm" />
        ) : (
          record?.quantity
        )}
      </span>
      <Button
        size="sm"
        variant="light"
        className="no-hover-effect"
        onClick={() => handleQuantityChange(record?.id, 1)}
        disabled={quantityLoading[record?.id]}
      >
        <PlusIcon size={13} />
      </Button>
    </div>
  );

  QuantityControl.propTypes = {
    record: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      quantity: PropTypes.number.isRequired,
    }).isRequired,
  };

  const columns = [
    {
      title: "Item Name",
      dataIndex: "itemName",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Quantity",
      render: (text, record) => <QuantityControl record={record} />,
    },
    {
      title: "Notes",
      dataIndex: "notes",
    },
    {
      title: "Actions",
      render: (record) => (
        <>
          <Link className="me-3" to="#" onClick={() => handleEditItem(record)}>
            <img src={EditIcon} alt="Edit" />
          </Link>
          <Link
            className="confirm-text"
            to="#"
            onClick={() => handleDeleteItem(record.id)}
          >
            <img src={DeleteIcon} alt="Delete" />
          </Link>
        </>
      ),
    },
  ];

  const fetchData = async ({ page, pageSize }) => {
    setLoading(true);
    try {
      const result = await getInventoryItems({ page, limit: pageSize, search });
      setData(result.data.items);
      setTotalItems(result.data.totalItems);
    } catch (error) {
      alertify.error(error?.response?.data?.message || "Failed to fetch inventory items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ page: 1, pageSize: 10 });
  }, [search]);

  const handleDeleteItem = (itemId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteInventoryItem(itemId);
          Swal.fire("Deleted!", "Item has been deleted.", "success");
          fetchData({ page: 1, pageSize: 10 });
        } catch (error) {
          alertify.error(error?.response?.data?.message || "Failed to delete item");
        }
      }
    });
  };

  const handleEditItem = (item) => {
    setEditData(item);
    setFormData({
      itemName: item.itemName,
      description: item.description,
      quantity: item.quantity,
      notes: item.notes,
    });
    setShowModal(true);
  };

  const handleAddItem = () => {
    setEditData(null);
    setFormData({
      itemName: "",
      description: "",
      quantity: 0,
      notes: "",
    });
    setShowModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ["itemName", "quantity"];
    const newErrors = {};
    let hasErrors = false;

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      if (editData) {
        await updateInventoryItem(editData.id, formData);
        alertify.success("Item updated successfully!");
      } else {
        await createInventoryItem(formData);
        alertify.success("Item added successfully!");
      }
      setShowModal(false);
      fetchData({ page: 1, pageSize: 10 });
    } catch (error) {
      alertify.error(error?.response?.data?.message || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Inventory List</h4>
              <h6>Manage inventory items</h6>
            </div>
            <div className="page-btn">
              <Link to="#" className="btn btn-added" onClick={handleAddItem}>
                <FeatherIcon icon="plus" className="me-1" />
                Add Item
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="row">
                <Tabletop value={search} OnChange={
                  (e) => setSearch(e.target.value)
                } />
              </div>
              <div className="table-responsive">
                <Table
                  isLoading={loading}
                  columns={columns}
                  response={{
                    data: data.map((item, index) => ({ ...item})),
                    totalDataCount: totalItems,
                  }}
                  fetchData={fetchData}
                  NoPagination={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header>
          <Modal.Title>{editData ? "Edit Item" : "Add Item"}</Modal.Title>
          <button type="button" className="btn-close me-1" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmit}>
            <Form.Group className="form-group">
              <Form.Label>Item Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.itemName}
                onChange={(e) =>
                  setFormData({ ...formData, itemName: e.target.value })
                }
                className={errors.itemName ? "is-invalid" : ""}
              />
              {errors.itemName && (
                <div className="invalid-feedback">{errors.itemName}</div>
              )}
            </Form.Group>

            <Form.Group className="form-group">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="form-group">
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })
                }
                className={errors.quantity ? "is-invalid" : ""}
              />
              {errors.quantity && (
                <div className="invalid-feedback">{errors.quantity}</div>
              )}
            </Form.Group>

            <Form.Group className="form-group">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <button
                type="submit"
                className="btn btn-submit me-2 d-flex align-items-center justify-content-center"
                disabled={isSubmitting}
                style={{ width: 140 }}
              >
                {
                  isSubmitting && (
                    <Spinner animation="border" size="sm" className="me-2" />
                  )
                }
                {editData ? "Update" : "Add"} Item
              </button>
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default InventoryCRUD;